interface Section {
  title: string;
  content: string;
  keyPoints?: string[];
  example?: string;
  commonError?: {
    error: string;
    correction: string;
  };
}

interface Lesson {
  id: number;
  title: string;
  content: {
    introduction: string;
    sections: Section[];
  };
}

interface LessonContentProps {
  lesson: Lesson;
}

function LessonContent({ lesson }: LessonContentProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 px-3 py-1 rounded-full mb-4">
          <span className="text-[#1A3A2E] text-sm font-bold">Lección {lesson.id}</span>
        </div>
        <h1 className="text-3xl font-bold text-[#1A3A2E] mb-4">{lesson.title}</h1>
        <p className="text-lg text-gray-700 leading-relaxed">{lesson.content.introduction}</p>
      </div>

      <div className="space-y-8">
        {lesson.content.sections.map((section, index) => (
          <div key={index} className="border-l-4 border-[#39FF14] pl-6">
            <h2 className="text-xl font-bold text-[#1A3A2E] mb-4">{section.title}</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{section.content}</p>

            {section.keyPoints && section.keyPoints.length > 0 && (
              <div className="bg-gradient-to-br from-[#1A3A2E]/5 to-[#39FF14]/5 rounded-lg p-6 mb-4">
                <h3 className="text-sm font-bold text-[#1A3A2E] mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-[#39FF14]"></i>
                  Puntos Clave
                </h3>
                <ul className="space-y-2">
                  {section.keyPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#39FF14] text-lg flex-shrink-0 mt-0.5"></i>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {section.example && (
              <div className="bg-gray-50 rounded-lg p-6 mb-4">
                <h3 className="text-sm font-bold text-[#1A3A2E] mb-3 flex items-center gap-2">
                  <i className="ri-file-list-line text-[#39FF14]"></i>
                  Ejemplo Práctico
                </h3>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {section.example}
                </pre>
              </div>
            )}

            {section.commonError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-sm font-bold text-red-900 mb-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  Error Común
                </h3>
                <p className="text-sm text-red-800 mb-3">
                  <strong>Error:</strong> {section.commonError.error}
                </p>
                <p className="text-sm text-green-800">
                  <strong>Corrección:</strong> {section.commonError.correction}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="bg-gradient-to-r from-[#1A3A2E] to-[#0F2419] rounded-lg p-6 text-center">
          <i className="ri-trophy-line text-4xl text-[#39FF14] mb-3"></i>
          <p className="text-white text-sm">
            Has completado la lección <strong>{lesson.id}: {lesson.title}</strong>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LessonContent;